// Chain of custody: releasing goods, accepting them, and what is in between.
//
// Mounted from AppMain when activeModule === 'custodyDesk'.
//
// THE FIRST TAB IS THE ONE THAT MATTERS
// -------------------------------------
// "Awaiting your confirmation" is what somebody opens in the morning. It is
// filtered by their role and excludes anything they released themselves,
// because they cannot accept that and a button that answers 403 is worse
// than no button.
//
// WHY RELEASE AND ACCEPT LOOK DIFFERENT
// -------------------------------------
// Releasing is composing: pick a route, pick items, name the carrier.
// Accepting is counting: one number per line, pre-filled with what was sent,
// and the moment a figure is changed the screen says what that means. The
// two jobs are done by different people in different places, so they do not
// share a form.
//
// A DIFFERENCE IS NOT AN ERROR
// ----------------------------
// If 94 arrive against 100 sent, that is a fact and the screen accepts it.
// What it does not do is let it pass silently: the variance is shown while
// typing, and the saved transfer carries an open discrepancy until somebody
// writes a sentence explaining it.

import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch } from './utils/api';
import { color, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip, ErrorBox, SkeletonCards } from './ui/kit';

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try {
      const j = await res.json();
      d = j.detail?.message || j.detail?.error || j.detail || d;
    } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: radius.sm, fontSize: 13.5,
  border: `1px solid ${color.borderStrong}`, fontFamily: 'inherit',
  boxSizing: 'border-box', background: '#fff', color: color.text,
};

const num = (v, dp = 3) =>
  v === null || v === undefined || v === ''
    ? '—'
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: dp });

function th(align = 'left') {
  return {
    padding: space(1), textAlign: align, fontWeight: 600,
    borderBottom: `1px solid ${color.border}`, whiteSpace: 'nowrap',
  };
}
function td(align = 'left') {
  return {
    padding: space(1), textAlign: align,
    borderBottom: `1px solid ${color.border}`,
  };
}

function age(hours) {
  if (hours === null || hours === undefined) return '—';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${Math.round(hours)} h`;
  return `${Math.round(hours / 24)} days`;
}

// ---------------------------------------------------------------------------

// Releasing goods: pick a route, pick what is going, name who is carrying it.
//
// WHY THE ROUTE IS CHOSEN FIRST
// -----------------------------
// The kind of hand-off decides who may release it, who must accept it, and
// which stores it runs between. Choosing it first means every later field
// can be narrowed, and the person never fills in a form they are not allowed
// to submit.
//
// WHY ITEMS ARE SEARCHED, NOT LISTED
// ----------------------------------
// There are 67 raw materials and 17 products. A dropdown of 84 things sorted
// by nothing is slower than typing three letters.
function ReleaseForm({ kinds, warehouses, onDone, onCancel, notify }) {
  const releasable = kinds.filter((k) => k.can_release);
  const [kind, setKind] = useState(releasable[0]?.kind || '');
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [reason, setReason] = useState('');
  const [carrier, setCarrier] = useState('');
  const [carrierPhone, setCarrierPhone] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [waybill, setWaybill] = useState('');
  const [lines, setLines] = useState([]);
  const [manifestId, setManifestId] = useState('');
  const [manifests, setManifests] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  const chosen = kinds.find((k) => k.kind === kind);

  // Raw materials and finished goods are different tables, and which one is
  // relevant depends entirely on the route: nobody ships paraffin to a
  // customer or cartons of gel to the production floor.
  const wantsRawMaterials = ['GOODS_IN', 'RAW_TO_PRODUCTION'].includes(kind);

  // A van load belongs to a delivery run. Without the link the same cartons
  // are tracked in two places that can disagree, and neither knows about the
  // other.
  const needsManifest = kind === 'SALES_TO_VEHICLE';
  // The last moment anybody here holds the goods. After this the
  // next person to read the batch number is the customer.
  const needsBatchCheck = kind === 'SALES_TO_VEHICLE';

  useEffect(() => {
    if (!needsManifest) { setManifests([]); return; }
    let cancelled = false;
    (async () => {
      const data = await req('/api/logistics/manifests?status=preparing')
        .catch(() => []);
      const list = Array.isArray(data) ? data : (data.items || data.manifests || []);
      if (!cancelled) setManifests(list);
    })();
    return () => { cancelled = true; };
  }, [needsManifest]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const url = wantsRawMaterials
          ? `/api/raw-materials/?size=25&search=${encodeURIComponent(term)}`
          : `/api/products/?size=25&search=${encodeURIComponent(term)}`;
        const data = await req(url).catch(() => ({ items: [] }));
        const items = Array.isArray(data) ? data : (data.items || []);
        if (!cancelled) {
          setResults(items.map((x) => ({
            id: x.id,
            name: x.name,
            sku: x.sku,
            unit: x.unit || x.uom || null,
            isRaw: wantsRawMaterials,
          })));
        }
      } finally { if (!cancelled) setSearching(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, wantsRawMaterials]);

  // Which batches of a product are actually on this shelf, soonest expiry
  // first. Keyed by product so a release with several lines keeps them apart.
  const [batchesFor, setBatchesFor] = useState({});

  const loadBatches = useCallback(async (productId) => {
    if (!fromId || !productId) return;
    try {
      const r = await req(
        `/api/batches/available?product_id=${productId}&warehouse_id=${fromId}`);
      setBatchesFor((prev) => ({ ...prev, [productId]: r.batches || [] }));
    } catch (e) {
      notify?.('Could not load the batches on this shelf', 'error');
    }
  }, [fromId, notify]);

  const addLine = (item) => {
    if (lines.some((l) => l.id === item.id)) {
      notify?.(`${item.name} is already on this release`, 'info');
      return;
    }
    setLines([...lines, { ...item, quantity: '', batch_id: '',
                          confirmed_batch_number: '',
                          confirmed_expiry_date: '' }]);
    if (!item.isRaw) loadBatches(item.id);
    setQuery('');
    setResults([]);
  };

  const send = async () => {
    if (!kind || !fromId || !toId) {
      notify?.('Pick the route and both stores', 'error'); return;
    }
    if (fromId === toId) {
      notify?.('A transfer needs two different stores', 'error'); return;
    }
    if (reason.trim().length < 3) {
      notify?.('Say why these goods are moving', 'error'); return;
    }
    if (needsManifest && !manifestId) {
      notify?.('Pick the delivery run these goods are being loaded for', 'error');
      return;
    }
    if (lines.length === 0) {
      notify?.('Add at least one item', 'error'); return;
    }
    const payload = { kind, from_warehouse_id: fromId, to_warehouse_id: toId,
                      reason: reason.trim(), lines: [],
                      manifest_id: manifestId || null,
                      carrier_name: carrier || null,
                      carrier_phone: carrierPhone || null,
                      vehicle_details: vehicle || null,
                      waybill_number: waybill || null };
    for (const l of lines) {
      const q = Number(l.quantity);
      if (!q || Number.isNaN(q) || q <= 0) {
        notify?.(`${l.name} needs a quantity above zero`, 'error'); return;
      }
      if (needsBatchCheck && !l.isRaw) {
        if (!l.batch_id) {
          notify?.(`Pick the batch of ${l.name} being loaded`, 'error'); return;
        }
        if (!l.confirmed_batch_number?.trim() || !l.confirmed_expiry_date) {
          notify?.(
            `Enter the batch number and expiry printed on the ${l.name} cartons`,
            'error');
          return;
        }
      }
      payload.lines.push(l.isRaw
        ? { raw_material_id: l.id, quantity: q, unit: l.unit }
        : { product_id: l.id, quantity: q, unit: l.unit,
            batch_id: l.batch_id || null,
            confirmed_batch_number: l.confirmed_batch_number?.trim() || null,
            confirmed_expiry_date: l.confirmed_expiry_date || null });
    }

    setBusy(true);
    try {
      const r = await req('/api/custody/dispatch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      notify?.(r.detail, 'success');
      onDone?.(r);
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (releasable.length === 0) {
    return (
      <Card>
        <Banner tone="info" title="Your role cannot release goods">
          Releasing is restricted by the kind of hand-off. Ask an
          administrator which role you need, or accept consignments other
          people release to you.
        </Banner>
        <div style={{ marginTop: space(2) }}>
          <Btn variant="secondary" onClick={onCancel}>Back</Btn>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ fontWeight: 650, fontSize: 15, marginBottom: space(1.5) }}>
        Release goods
      </div>

      {needsManifest ? (
        <Banner tone="info" title="This records the hand-over, not a stock movement">
          Stock for a delivery left the sales warehouse when the invoice was
          raised. Loading the van does not deduct it again &mdash; what is
          recorded here is who handed the goods over and which driver took
          them, which the manifest never captured.
        </Banner>
      ) : (
        <Banner tone="warning" title="These goods leave the store immediately">
          They arrive nowhere until somebody at the receiving end counts them
          in. Until then they are in transit and belong to neither store, which
          is what makes a consignment that never arrives impossible to miss.
        </Banner>
      )}

      {needsManifest && (
        <div style={{ marginTop: space(1.5) }}>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
            Delivery run <span style={{ color: color.danger }}>(required)</span>
          </div>
          <select style={inputStyle} value={manifestId}
                  onChange={(e) => setManifestId(e.target.value)}>
            <option value="">Select the run being loaded…</option>
            {manifests.map((m) => (
              <option key={m.id} value={m.id}>
                {m.manifest_number}
                {m.delivery_date ? ` · ${String(m.delivery_date).slice(0, 10)}` : ''}
                {m.driver_name ? ` · ${m.driver_name}` : ''}
              </option>
            ))}
          </select>
          {manifests.length === 0 && (
            <div style={{ fontSize: 12.5, color: color.textMuted, marginTop: 4 }}>
              No runs are being prepared. Create the manifest under Logistics
              first, then load it here.
            </div>
          )}
        </div>
      )}

      <div style={{
        display: 'grid', gap: space(1.5), marginTop: space(1.5),
        gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
      }}>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>Hand-off</div>
          <select style={inputStyle} value={kind}
                  onChange={(e) => { setKind(e.target.value); setLines([]); }}>
            {releasable.map((k) => (
              <option key={k.kind} value={k.kind}>{k.label}</option>
            ))}
          </select>
        </label>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>From</div>
          <select style={inputStyle} value={fromId}
                  onChange={(e) => setFromId(e.target.value)}>
            <option value="">Select a store…</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </label>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>To</div>
          <select style={inputStyle} value={toId}
                  onChange={(e) => setToId(e.target.value)}>
            <option value="">Select a store…</option>
            {warehouses.filter((w) => w.id !== fromId).map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </label>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>Why</div>
          <input style={inputStyle} value={reason}
                 onChange={(e) => setReason(e.target.value)}
                 placeholder="e.g. replenishing the sales store" />
        </label>
      </div>

      {chosen && (
        <div style={{ fontSize: 12.5, color: color.textSecondary, marginTop: space(1) }}>
          Will be accepted by: {chosen.accept_roles.join(', ')}. You cannot
          accept it yourself.
        </div>
      )}

      {/* Items */}
      <div style={{ marginTop: space(2), fontWeight: 650, fontSize: 14 }}>
        {wantsRawMaterials ? 'Raw materials' : 'Finished goods'} ({lines.length})
      </div>
      <div style={{ marginTop: space(1), position: 'relative' }}>
        <input style={inputStyle} value={query}
               onChange={(e) => setQuery(e.target.value)}
               placeholder={`Search ${wantsRawMaterials ? 'raw materials' : 'products'} by name or code…`} />
        {(results.length > 0 || searching) && (
          <div style={{
            position: 'absolute', zIndex: 20, left: 0, right: 0,
            background: '#fff', border: `1px solid ${color.borderStrong}`,
            borderRadius: radius.sm, marginTop: 4, maxHeight: 240,
            overflowY: 'auto',
          }}>
            {searching && (
              <div style={{ padding: space(1), fontSize: 12.5, color: color.textMuted }}>
                Searching…
              </div>
            )}
            {results.map((r) => (
              <div key={r.id} onClick={() => addLine(r)}
                   style={{
                     padding: space(1), cursor: 'pointer', fontSize: 13,
                     borderBottom: `1px solid ${color.border}`,
                   }}>
                {r.name}
                <span style={{ color: color.textMuted }}>
                  {r.sku ? ` · ${r.sku}` : ''}{r.unit ? ` · ${r.unit}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {lines.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: space(1.5) }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: color.bg }}>
                <th style={th()}>Item</th>
                <th style={th()}>Unit</th>
                <th style={th('right')}>Quantity</th>
                <th style={th('right')}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.id}>
                  <td style={td()}>{l.name}</td>
                  <td style={{ ...td(), color: color.textSecondary }}>{l.unit || '—'}</td>
                  <td style={{ ...td('right'), width: 140 }}>
                    <input style={{ ...inputStyle, textAlign: 'right' }}
                           inputMode="decimal" value={l.quantity}
                           onChange={(e) => {
                             const next = [...lines];
                             next[i] = { ...l, quantity: e.target.value };
                             setLines(next);
                           }} />
                  </td>
                  <td style={td('right')}>
                    <Btn size="sm" variant="secondary"
                         onClick={() => setLines(lines.filter((x) => x.id !== l.id))}>
                      Remove
                    </Btn>
                  </td>
                </tr>
              ))}
              {needsBatchCheck && lines.filter((l) => !l.isRaw).map((l) => {
                const i = lines.findIndex((x) => x.id === l.id);
                const opts = batchesFor[l.id] || [];
                const set = (patch) => {
                  const next = [...lines];
                  next[i] = { ...lines[i], ...patch };
                  setLines(next);
                };
                return (
                  <tr key={`${l.id}-batch`}>
                    <td style={td()} colSpan={4}>
                      <div style={{ fontSize: 12, color: color.textSecondary,
                                    marginBottom: 6 }}>
                        <strong>{l.name}</strong> &mdash; pick the batch being
                        loaded, then type what is printed on the cartons. Do not
                        copy it from the list: the point of the check is to
                        catch the shelf and the record disagreeing.
                      </div>
                      <div style={{ display: 'grid', gap: 8,
                                    gridTemplateColumns:
                                      'repeat(auto-fit, minmax(150px, 1fr))' }}>
                        <select style={inputStyle} value={l.batch_id || ''}
                                onChange={(e) => set({ batch_id: e.target.value })}>
                          <option value="">Batch on the shelf&hellip;</option>
                          {opts.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.batch_number}
                              {b.expiry_date ? ` \u00b7 exp ${String(b.expiry_date).slice(0, 10)}` : ''}
                              {b.on_hand != null ? ` \u00b7 ${b.on_hand}` : ''}
                            </option>
                          ))}
                        </select>
                        <input style={inputStyle}
                               value={l.confirmed_batch_number || ''}
                               placeholder="Batch no. on the carton"
                               onChange={(e) => set({ confirmed_batch_number: e.target.value })} />
                        <input style={inputStyle} type="date"
                               value={l.confirmed_expiry_date || ''}
                               onChange={(e) => set({ confirmed_expiry_date: e.target.value })} />
                      </div>
                      {opts.length === 0 && (
                        <div style={{ fontSize: 11.5, color: color.textMuted,
                                      marginTop: 6 }}>
                          No batch of this product has stock in the sending
                          store.
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Who is carrying it. Free text because a third-party driver has no
          login, and inventing accounts for people who will never sign in
          would be worse than recording their name. */}
      <div style={{
        display: 'grid', gap: space(1.5), marginTop: space(2),
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      }}>
        {[['Carrier / driver', carrier, setCarrier, 'e.g. Musa Ibrahim'],
          ['Phone', carrierPhone, setCarrierPhone, ''],
          ['Vehicle', vehicle, setVehicle, 'e.g. Hiace ABC-123 XY'],
          ['Waybill number', waybill, setWaybill, '']].map(
          ([label, value, setter, placeholder]) => (
            <label key={label}>
              <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
                {label}
              </div>
              <input style={inputStyle} value={value}
                     onChange={(e) => setter(e.target.value)}
                     placeholder={placeholder} />
            </label>
          ))}
      </div>

      <div style={{ marginTop: space(2), display: 'flex', gap: space(1) }}>
        <Btn onClick={send} disabled={busy}>Release goods</Btn>
        <Btn variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Btn>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function ReceiveForm({ run, onDone, onCancel, notify }) {
  const [counts, setCounts] = useState(() => {
    const seed = {};
    run.lines.forEach((l) => { seed[l.line_id] = String(l.quantity); });
    return seed;
  });
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const variances = run.lines
    .map((l) => ({ line: l, diff: Number(counts[l.line_id] || 0) - Number(l.quantity) }))
    .filter((v) => Math.abs(v.diff) > 1e-9);

  const save = async () => {
    const lines = {};
    for (const [k, v] of Object.entries(counts)) {
      const n = Number(v);
      if (v === '' || Number.isNaN(n) || n < 0) {
        notify?.('Every line needs a quantity of zero or more', 'error');
        return;
      }
      lines[k] = n;
    }
    setBusy(true);
    try {
      const r = await req(`/api/custody/${run.transfer_id}/receive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lines, receipt_notes: notes || null }),
      });
      notify?.(r.detail, r.has_discrepancy ? 'warning' : 'success');
      onDone?.(r);
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <div style={{ fontWeight: 650, fontSize: 15 }}>
        Accept {run.transfer_number}
      </div>
      <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: space(1.5) }}>
        {run.from_warehouse} → {run.to_warehouse} · released by {run.dispatched_by}
        {run.carrier_name ? ` · carried by ${run.carrier_name}` : ''}
        {run.vehicle_details ? ` (${run.vehicle_details})` : ''}
      </div>

      <Banner tone="info" title="Count what actually arrived">
        These figures are what was <em>sent</em>. Change any that differ — the
        count on the bay is the fact, and a difference is recorded for review
        rather than refused.
      </Banner>

      <div style={{ overflowX: 'auto', marginTop: space(1.5) }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: color.bg }}>
              <th style={th()}>Item</th>
              <th style={th()}>Batch</th>
              <th style={th()}>Unit</th>
              <th style={th('right')}>Sent</th>
              <th style={th('right')}>Arrived</th>
              <th style={th('right')}>Difference</th>
            </tr>
          </thead>
          <tbody>
            {run.lines.map((l) => {
              const diff = Number(counts[l.line_id] || 0) - Number(l.quantity);
              const off = Math.abs(diff) > 1e-9;
              return (
                <tr key={l.line_id}>
                  <td style={td()}>{l.item}</td>
                  <td style={{ ...td(), color: color.textSecondary }}>{l.batch_number || '—'}</td>
                  <td style={{ ...td(), color: color.textSecondary }}>{l.unit || '—'}</td>
                  <td style={td('right')}>{num(l.quantity)}</td>
                  <td style={{ ...td('right'), width: 140 }}>
                    <input style={{ ...inputStyle, textAlign: 'right' }}
                           inputMode="decimal"
                           value={counts[l.line_id] ?? ''}
                           onChange={(e) => setCounts({ ...counts, [l.line_id]: e.target.value })} />
                  </td>
                  <td style={{
                    ...td('right'),
                    color: off ? (diff < 0 ? color.danger : color.warning) : color.textMuted,
                    fontWeight: off ? 600 : 400,
                  }}>
                    {off ? (diff > 0 ? '+' : '') + num(diff) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {variances.length > 0 && (
        <div style={{ marginTop: space(1.5) }}>
          <Banner tone="warning" title={`${variances.length} line(s) differ from what was sent`}>
            This will be recorded as an open discrepancy. The difference is not
            given back to {run.from_warehouse} and not credited to{' '}
            {run.to_warehouse} — somebody will have to explain where it went.
          </Banner>
        </div>
      )}

      <div style={{ marginTop: space(2) }}>
        <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
          Notes on the condition it arrived in
        </div>
        <input style={inputStyle} value={notes}
               onChange={(e) => setNotes(e.target.value)}
               placeholder="e.g. one carton crushed, seal intact" />
      </div>

      <div style={{ marginTop: space(2), display: 'flex', gap: space(1) }}>
        <Btn onClick={save} disabled={busy}>Confirm receipt</Btn>
        <Btn variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Btn>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function RunCard({ run, onAccept, showAccept }) {
  const stale = (run.hours_in_transit || 0) > 24;
  return (
    <Card>
      <div style={{
        display: 'flex', justifyContent: 'space-between', gap: space(1),
        flexWrap: 'wrap', alignItems: 'flex-start',
      }}>
        <div>
          <div style={{ display: 'flex', gap: space(1), alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 650, fontSize: 14.5 }}>{run.transfer_number}</span>
            <Chip tone="info">{run.kind_label || run.kind}</Chip>
            <Chip tone={stale ? 'danger' : 'neutral'}>
              in transit {age(run.hours_in_transit)}
            </Chip>
          </div>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginTop: 3 }}>
            {run.from_warehouse} → {run.to_warehouse} · released by {run.dispatched_by}
          </div>
          <div style={{ fontSize: 12.5, color: color.textSecondary }}>
            {run.carrier_name ? `Carrier ${run.carrier_name}` : 'No carrier named'}
            {run.vehicle_details ? ` · ${run.vehicle_details}` : ''}
            {run.waybill_number ? ` · waybill ${run.waybill_number}` : ''}
          </div>
          {run.reason && (
            <div style={{ fontSize: 12.5, color: color.textMuted, marginTop: 3 }}>
              {run.reason}
            </div>
          )}
        </div>
        {showAccept && (
          <Btn size="sm" onClick={() => onAccept(run)}>Count &amp; accept</Btn>
        )}
      </div>

      <div style={{ overflowX: 'auto', marginTop: space(1.5) }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: color.bg }}>
              <th style={th()}>Item</th>
              <th style={th()}>Batch</th>
              <th style={th()}>Unit</th>
              <th style={th('right')}>Quantity</th>
            </tr>
          </thead>
          <tbody>
            {run.lines.map((l) => (
              <tr key={l.line_id}>
                <td style={td()}>{l.item}</td>
                <td style={{ ...td(), color: color.textSecondary }}>{l.batch_number || '—'}</td>
                <td style={{ ...td(), color: color.textSecondary }}>{l.unit || '—'}</td>
                <td style={{ ...td('right'), fontWeight: 600 }}>{num(l.quantity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {stale && (
        <div style={{ marginTop: space(1), fontSize: 12.5, color: color.danger }}>
          Dispatched more than a day ago and still not accepted. These goods are
          in neither store.
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

function Discrepancies({ notify, onChanged }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setErr('');
    try { setRows(await req('/api/custody/discrepancies')); }
    catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (row) => {
    const resolution = window.prompt(
      `What happened to the difference on ${row.transfer_number}?\n\n`
      + 'This explains the variance; it does not change the quantities. '
      + 'Correcting stock is a separate adjustment.\n\n'
      + '(at least 10 characters)', '');
    if (resolution === null) return;
    if (resolution.trim().length < 10) {
      notify?.('Write a sentence — a resolution nobody can read is not one', 'error');
      return;
    }
    setBusy(row.transfer_id);
    try {
      const r = await req(`/api/custody/${row.transfer_id}/resolve-discrepancy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolution: resolution.trim() }),
      });
      notify?.(r.detail, 'success');
      await load();
      onChanged?.();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  if (err) return <ErrorBox msg={err} />;
  if (!rows) return <SkeletonCards n={2} />;
  if (rows.length === 0) {
    return (
      <Banner tone="success" title="Nothing unexplained">
        Every transfer that arrived short or over has been accounted for.
      </Banner>
    );
  }

  return (
    <div style={{ display: 'grid', gap: space(2) }}>
      <Banner tone="warning" title={`${rows.length} unexplained difference${rows.length === 1 ? '' : 's'}`}>
        Goods that were sent and did not arrive, or arrived that were not sent.
        Each one is stock nobody can account for until somebody says what
        happened.
      </Banner>
      {rows.map((row) => (
        <Card key={row.transfer_id}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', gap: space(1),
            flexWrap: 'wrap', alignItems: 'flex-start',
          }}>
            <div>
              <div style={{ fontWeight: 650, fontSize: 14.5 }}>{row.transfer_number}</div>
              <div style={{ fontSize: 12.5, color: color.textSecondary }}>
                {row.from_warehouse} → {row.to_warehouse}
              </div>
              <div style={{ fontSize: 12.5, color: color.textSecondary }}>
                released by {row.dispatched_by} · accepted by {row.received_by}
              </div>
            </div>
            <Btn size="sm" onClick={() => resolve(row)}
                 disabled={busy === row.transfer_id}>
              Explain
            </Btn>
          </div>
          <div style={{ overflowX: 'auto', marginTop: space(1.5) }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: color.bg }}>
                  <th style={th()}>Item</th>
                  <th style={th('right')}>Sent</th>
                  <th style={th('right')}>Arrived</th>
                  <th style={th('right')}>Difference</th>
                </tr>
              </thead>
              <tbody>
                {row.lines.map((l) => (
                  <tr key={l.line_id}>
                    <td style={td()}>{l.item}</td>
                    <td style={td('right')}>{num(l.sent)}</td>
                    <td style={td('right')}>{num(l.received)}</td>
                    <td style={{
                      ...td('right'), fontWeight: 650,
                      color: l.difference < 0 ? color.danger : color.warning,
                    }}>
                      {l.difference > 0 ? '+' : ''}{num(l.difference)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function CustodyDesk({ notify }) {
  const [tab, setTab] = useState('mine');
  const [mine, setMine] = useState(null);
  const [all, setAll] = useState(null);
  const [kinds, setKinds] = useState([]);
  const [receiving, setReceiving] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const [m, a, k, w] = await Promise.all([
        req('/api/custody/awaiting-me'),
        req('/api/custody/in-transit'),
        req('/api/custody/kinds').catch(() => []),
        req('/api/warehouses/?size=200').catch(() => ({ items: [] })),
      ]);
      setMine(m); setAll(a); setKinds(k);
      const wlist = Array.isArray(w) ? w : (w.items || []);
      setWarehouses(wlist.map((x) => ({ id: x.id, name: x.name }))
                         .filter((x) => x.id));
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (err) return <ErrorBox msg={err} />;
  if (!mine || !all) return <SkeletonCards n={3} />;

  if (releasing) {
    return (
      <ReleaseForm
        kinds={kinds} warehouses={warehouses} notify={notify}
        onCancel={() => setReleasing(false)}
        onDone={() => { setReleasing(false); load(); }}
      />
    );
  }

  if (receiving) {
    return (
      <ReceiveForm
        run={receiving} notify={notify}
        onCancel={() => setReceiving(null)}
        onDone={() => { setReceiving(null); load(); }}
      />
    );
  }

  const canRelease = kinds.filter((k) => k.can_release);

  const tabs = [
    ['mine', `Awaiting your confirmation (${mine.length})`],
    ['transit', `Everything in transit (${all.length})`],
    ['discrepancies', 'Unexplained differences'],
  ];

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', gap: space(1),
        marginBottom: space(2), flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: space(1), flexWrap: 'wrap' }}>
          {tabs.map(([k, label]) => (
            <Btn key={k} size="sm" variant={tab === k ? 'primary' : 'secondary'}
                 onClick={() => setTab(k)}>{label}</Btn>
          ))}
        </div>
        {canRelease.length > 0 && (
          <Btn onClick={() => setReleasing(true)}>+ Release goods</Btn>
        )}
      </div>

      {tab === 'mine' && (
        <div style={{ display: 'grid', gap: space(2) }}>
          {mine.length === 0 ? (
            <Banner tone="success" title="Nothing waiting for you">
              Goods you are allowed to accept will appear here when somebody
              releases them. Anything you released yourself is excluded —
              you cannot sign for your own delivery.
            </Banner>
          ) : (
            <Banner tone="info" title={`${mine.length} consignment${mine.length === 1 ? '' : 's'} waiting to be counted`}>
              These goods have left their store and arrived nowhere. They stay
              in transit, belonging to neither side, until you count them in.
            </Banner>
          )}
          {mine.map((run) => (
            <RunCard key={run.transfer_id} run={run} showAccept
                     onAccept={setReceiving} />
          ))}
        </div>
      )}

      {tab === 'transit' && (
        <div style={{ display: 'grid', gap: space(2) }}>
          {all.length === 0 ? (
            <Banner tone="success" title="Nothing in transit">
              Every consignment that has left a store has been accounted for at
              the other end.
            </Banner>
          ) : (
            <Banner tone="info" title={`${all.length} consignment${all.length === 1 ? '' : 's'} in transit`}>
              Stock that has left one store and not yet arrived at another. It
              is in nobody&apos;s balance, which is the point: a vehicle that
              never arrives cannot look like full shelves.
            </Banner>
          )}
          {all.map((run) => (
            <RunCard key={run.transfer_id} run={run}
                     showAccept={mine.some((m) => m.transfer_id === run.transfer_id)}
                     onAccept={setReceiving} />
          ))}
        </div>
      )}

      {tab === 'discrepancies' && (
        <Discrepancies notify={notify} onChanged={load} />
      )}

      {canRelease.length > 0 && (
        <details style={{ marginTop: space(3) }}>
          <summary style={{ cursor: 'pointer', fontSize: 12.5, color: color.textSecondary }}>
            Hand-offs your role can release ({canRelease.length})
          </summary>
          <div style={{ marginTop: space(1) }}>
            {canRelease.map((k) => (
              <div key={k.kind} style={{ fontSize: 12.5, color: color.textSecondary, padding: '3px 0' }}>
                <strong>{k.label}</strong> — accepted by {k.accept_roles.join(', ')}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
